/**
 * progressive-decode.js — start watching a file the browser cannot decode
 * after ONE segment, instead of after the whole transcode.
 *
 * Why this exists (measured, 2026-08-05, prod telemetry + benchmarks):
 *   - 44% of sessions that pick a file never get playback at all.
 *   - Whole-file transcode of a 90-minute 1080p film is ~2.3 h even at the
 *     fastest usable preset, and would emit ~6 GB — past the point where an
 *     in-memory Blob is readable. So it is not "slow", it is impossible.
 *   - WebCodecs cannot rescue it: VideoDecoder refuses MPEG-4 ASP, MS-MPEG4,
 *     WMV2, VC-1 and MPEG-2, which is exactly the set that fails.
 * Segmenting into MSE was measured to give a first frame 3.9x-6.4x sooner.
 *
 * The one hard constraint, also measured: the encoder must outrun playback.
 *   640x480 native   3.73x realtime   stays ahead
 *   1080p  native    0.64x realtime   FALLS BEHIND
 *   1080p  ->720p    0.53x realtime   FALLS BEHIND (scaling costs what it saves)
 *   1080p  ->480p    1.65x realtime   stays ahead
 * So throughput is REPORTED to the caller after each segment rather than
 * assumed; the caller decides what to offer. Never silently step the picture
 * down — that is the user's call (feedback_no_silent_quality_downgrade).
 *
 *   const p = ProgressiveDecode.start(file, {
 *       duration, segment: 15, scale: 0,
 *       onFirstSegment({url, xRealtime}), onProgress({readySec, xRealtime}),
 *       onDone(), onError(e)
 *   });
 *   p.readySeconds(); p.throughput(); p.setScale(854); p.stop();
 */
(function () {
    'use strict';

    /* Profile/level are PINNED so the MSE codec string is knowable up front.
       Left to itself libx264 picks High, and a SourceBuffer created with the
       wrong string fails at addSourceBuffer or silently refuses appends.

       ⚠️ The codec string is a PROMISE about the segments we are going to
       append, and it must match what the SOURCE contains. Declaring
       `mp4a.40.2` for a file with no audio track made Chrome reject the very
       first init segment — "Initialization segment misses expected aac track"
       — which reached the tool as an `error` Event with no `.message`, i.e. as
       an EMPTY error string. Every silent film, screen capture without sound,
       GIF-turned-mp4 and camera export took that path. Hence AUDIO_MIME /
       VIDEO_MIME and the per-run choice below. */
    var AUDIO_MIME = 'video/mp4; codecs="avc1.4D4028,mp4a.40.2"';
    var VIDEO_MIME = 'video/mp4; codecs="avc1.4D4028"';
    var MIME = AUDIO_MIME;
    var VIDEO_ARGS = ['-c:v', 'libx264', '-preset', 'ultrafast', '-crf', '28',
                      '-profile:v', 'main', '-level', '4.0', '-pix_fmt', 'yuv420p'];
    /* Audio parameters must be IDENTICAL across segments: a SourceBuffer that
       gets a different sample rate or channel count mid-stream rejects the
       append. Pinned rather than inherited from the source. */
    var AUDIO_ARGS = ['-c:a', 'aac', '-b:a', '128k', '-ac', '2', '-ar', '48000'];
    /* empty_moov + frag_keyframe = fragmented mp4 MSE can take. */
    var MUX_ARGS = ['-sn', '-movflags', '+frag_keyframe+empty_moov+default_base_moof'];

    function isSupported() {
        try {
            return !!(window.MediaSource && window.MediaSource.isTypeSupported(AUDIO_MIME) &&
                      window.MediaSource.isTypeSupported(VIDEO_MIME) &&
                      window.FFmpegProcessor);
        } catch (_) { return false; }
    }

    /* Deadlines count VISIBLE time (timbrica-core.js __visibleTimeout).
       Chromium does not open a MediaSource in a hidden tab that has not played
       media yet; it waits for the tab to come back. On wall-clock time the
       20 s below fired while the viewer was in another tab — 28 sessions in
       30 days ended on «sourceopen_timeout», all four attempts of the
       22.09.2026 report among them. Without the core (it failed to load) the
       old clock is kept. */
    function visibleDeadline(fn, ms) {
        if (typeof window.__visibleTimeout === 'function') return window.__visibleTimeout(fn, ms);
        var h = setTimeout(fn, ms);
        return function () { clearTimeout(h); };
    }

    /* ── Decoder threads on the multi-threaded core ────────────────────────
       Only where the page runs the MT core (cross-origin isolated + the tool
       opted in: config/coep_test.php). The wrapper gives the ENCODER threads by
       itself (an output `-threads` at the exec door); the DECODER gets them
       only from a `-threads` BEFORE `-i`, and once any `-threads` is present
       the door stops adding its own — so both go together.
       A codec gets decoder threads only if the gain was measured THROUGH this
       lane (segments with sound → fMP4 → MediaSource), not on a bare exec: the
       bare matrix of 23.09 promised MJPEG ×1.3 and MPEG-2 ×1.7–3.8 from decoder
       threads, the lane gave them ×1.05–1.4 on top of the encoder threads.
       Numbers, method and what is still missing: docs/why/primitives/progressive-decode.md.
       AV1 never: libdav1d traps «memory access out of bounds» on this core.

       Measured 09–10.10.2026 through this lane (ProgressiveDecode.start on the
       real page, 2 × 15-s segments with sound, two rounds, Chrome 155, 32
       cores, 4 threads each side), camera-like 60-s footage: the MT page with
       decoder threads against today's single-threaded page, and in brackets
       what decoder threads add on top of the encoder threads the door gives:
           HEVC 1080p60 + AAC          ×3.9 / ×3.9    (×3.1)
           HEVC 4K30 → 1280 + AAC      ×3.7 / ×4.6    (×3.4–5.4)
           H.264 4:2:2 10-bit + AAC    ×4.5 / ×6.9    (×2.8–3.0)
           MPEG-4 Part 2 + MP3         ×3.3 / ×3.9    (×1.2–1.3)
           MPEG-2 + MP2                ×3.1 / ×3.2    (×1.07 / ×1.4 — not consistent)
           MJPEG + PCM                 ×3.1 / ×3.0    (×1.05–1.1 — nothing)
       A codec is listed when decoder threads gained ≥ ×1.15 in both rounds; the
       value is the smaller round's gain over the single-threaded page. */
    var MT_DECODER_GAIN = { hevc: 3.7, h264: 4.5, mpeg4: 3.3 };
    /* The MT probe: seconds of video, and its frame bounds (see measure()). */
    var MT_PROBE_S = 2, MT_PROBE_MIN = 30, MT_PROBE_MAX = 120;
    function cores() { return Math.max(1, (typeof navigator !== 'undefined' && navigator.hardwareConcurrency) || 4); }
    /* Half the cores, at most 4: WebAssembly threads wait for one another by
       spinning, so an oversubscribed CPU turns them from a gain into a loss. */
    function threadCount() { return Math.max(2, Math.min(4, Math.floor(cores() / 2))); }
    function decoderThreads(vcodec) {
        var c = String(vcodec || '').toLowerCase();
        if (!window.__ffmpegMtActive || !c || c === 'av1') return false;
        /* A/B on a live page (the caller sets it; null = the table decides). */
        var force = window.__progressiveThreads;
        if (force === 'off') return false;
        if (force === 'all') return true;
        return !!MT_DECODER_GAIN[c];
    }
    function threadArgs(vcodec) {
        if (!decoderThreads(vcodec)) return { pre: [], post: [] };
        var n = String(threadCount());
        return { pre: ['-threads', n], post: ['-threads', n] };
    }
    /* What the same job would do on a Premium page of THIS device: the rate
       measured here times the codec's measured gain, capped at half the cores.
       null = nothing honest to promise: a codec without a measured gain, a
       1–3-core device, or one the MT core never loads on (below 8 GB — the
       loader's own gate — or a browser that cannot be cross-origin isolated
       by `COEP: credentialless`: Safari). */
    function mtEstimate(x, vcodec) {
        var f = MT_DECODER_GAIN[String(vcodec || '').toLowerCase()];
        var c = cores();
        if (!f || !(x > 0) || c < 4) return null;
        try {
            /* The loader's own rule: a REPORTED size under 8 GB skips the MT core;
               an unreported one (0 here) does not (ffmpeg-processor.js _iso). */
            var dm = +((typeof navigator !== 'undefined' && navigator.deviceMemory) || 0);
            if (dm > 0 && dm < 8) return null;
            if (!(window.HTMLIFrameElement && 'credentialless' in window.HTMLIFrameElement.prototype)) return null;
        } catch (_) { return null; }
        return x * Math.min(f, c / 2);
    }

    /* `crf` — a caller that shows the picture NEXT TO an exact frame (the
       before/after split of /enhance-video) cannot have the transcode add its own
       blocks to the «before» side; the player's default stays 28. */
    function segmentVideoArgs(scale, crf) {
        var a = [];
        if (scale) a = a.concat(['-vf', "scale='min(" + scale + ",iw)':-2"]);
        var v = VIDEO_ARGS.slice();
        if (+crf > 0) { var ci = v.indexOf('-crf'); if (ci >= 0) v[ci + 1] = String(Math.round(+crf)); }
        return a.concat(v);
    }

    /* ── Price the job BEFORE promising it ─────────────────────────────────
       The tool used to show «playback will start in a moment» and then take
       whatever it took. For a 4K60 HEVC action-camera file on a device without
       an HEVC decoder that was ~30 minutes for the first 15 s at full size, and
       3.5 minutes even at 720p (measured 23.09.2026, this build, one thread):

           source            decode only   → 640p/30   → 1280p   → 1920p   → full
           HEVC 1080p60        0.18x         0.19x       0.11x     0.12x
           HEVC 4K60           0.075x        0.073x      0.069x    0.045x    0.008x

       The software HEVC decoder is the floor; no encoder setting lifts it. So
       the honest move is to MEASURE on this device, on this file, through the
       same arguments a segment uses, and let the caller say the real number.
       A handful of frames is enough: the per-frame decode dominates for exactly
       the sources where the answer matters, and for cheap sources the fixed
       overhead makes the estimate pessimistic, never optimistic.
       Returns { x, frames, ms } — x = seconds of video per second of work. */
    async function measure(file, opts) {
        opts = opts || {};
        var fp = window.FFmpegProcessor;
        if (!fp) throw new Error('engine_unavailable');
        if (!fp.isReady()) await fp.init();
        var fps = (+opts.fps > 0 && +opts.fps < 300) ? +opts.fps : 30;
        /* On the multi-threaded core a handful of frames measures the pipeline
           FILLING, not the work: the decoder holds a frame per thread and the
           threaded x264 several more, so 8–16 frames end before the first
           steady progress line. Measured through this lane (HEVC 1080p60, MT,
           decoder threads): 15 frames said 0.27×, the first real segment ran
           0.82× — a «wait ~4 min» card over a 20-second job. Two seconds of
           video put the first progress line past the fill; the rate from it to
           the end is then the segment's rate (docs/why/primitives/progressive-decode.md).
           The single-threaded probe is unchanged. */
        var mt = !!window.__ffmpegMtActive;
        var frames = mt ? Math.max(MT_PROBE_MIN, Math.min(MT_PROBE_MAX, Math.round(fps * MT_PROBE_S)))
                        : Math.max(8, Math.min(16, Math.round(fps * 0.25)));
        var th = threadArgs(opts.vcodec);
        var args = ['-an', '-sn', '-frames:v', String(frames)].concat(segmentVideoArgs(+opts.scale || 0), th.post);
        /* A short run is mostly start-up: opening the file, the decoder and
           x264, and the first keyframe. Measured on 4K60 HEVC, 15 frames took
           6.0 s while the same frames in a running segment cost 3.6 s — the
           whole-run figure promised 6 minutes for a 3.6-minute wait. ffmpeg's
           own progress lines (`frame=  N`, every ~0.5 s) give the rate AFTER
           start-up; the whole run is the fallback when it ends before the first
           line (fast sources, where a pessimistic figure costs nothing). Own
           listener only for this exec — nothing else runs on the engine while
           the ladder is deciding. */
        var marks = [], ff = null, cap = null;
        try {
            ff = fp._getFFmpeg && fp._getFFmpeg();
            cap = function (ev) {
                var m = /frame=\s*(\d+)/.exec(String((ev && ev.message) || ''));
                if (m) marks.push([(window.performance || Date).now(), +m[1]]);
            };
            if (ff && ff.on) ff.on('log', cap);
        } catch (_) { ff = null; }
        var t0 = (window.performance || Date).now(), out;
        try {
            out = await fp.processStreaming(file, args, 'measure.mp4', undefined, th.pre.concat(['-ss', String(+opts.at || 0)]));
        } finally {
            try { if (ff && ff.off && cap) ff.off('log', cap); } catch (_) {}
        }
        var t1 = (window.performance || Date).now();
        var ms = Math.max(1, t1 - t0);
        /* A probe, not a result: without this the page's blob auto-tracker
           counts these 15 frames as the tool's output (tool_complete fired
           before anything played, measured on the first run of this code). */
        try { if (out && out.blob && window.__skipBlobAutoTrack) window.__skipBlobAutoTrack(out.blob); } catch (_) {}
        try { if (out && out.url) URL.revokeObjectURL(out.url); } catch (_) {}
        var x = (frames / fps) / (ms / 1000), steady = false;
        for (var i = 0; i < marks.length; i++) {
            var f0 = marks[i][1], span = t1 - marks[i][0];
            if (f0 >= 1 && f0 < frames && span > 250) {
                x = ((frames - f0) / fps) / (span / 1000);
                steady = true;
                break;
            }
        }
        return { x: x, frames: frames, ms: Math.round(ms), steady: steady, mt: mt, threads: th.pre.length > 0 };
    }

    function start(file, opts) {
        opts = opts || {};
        var segLen = Math.max(4, +opts.segment || 15);
        var duration = +opts.duration || 0;
        var scale = +opts.scale || 0;
        var crf = +opts.crf || 0;
        /* `startAt` — play from a point of the FILE (the tool's own scrubber), not
           from its start. The element's clock starts at 0 there: readySec and the
           MediaSource duration are measured from startAt; the caller adds it back. */
        var startAt = Math.max(0, +opts.startAt || 0);
        if (duration && startAt >= duration - 0.5) startAt = 0;
        var stopped = false, done = false, stalledOnBuffer = false, paused = false, failed = false;
        var barren = 0, atEof = false, engineError = null;
        var readySec = 0, lastX = 0, segCount = 0;
        var ms = null, sb = null, url = '';
        /* Default true keeps every existing caller on the old string; the
           caller that ran the probe (and therefore KNOWS) passes the truth. */
        var hasAudio = opts.hasAudio !== false;
        var mime = hasAudio ? AUDIO_MIME : VIDEO_MIME;
        /* Where the viewer actually is. A MediaSource has a byte quota, so the
           only safe thing to drop is video already behind the playhead — and
           this module does not own the element. The caller hands over a reader
           once it has mounted its player (before any append: segment 0 takes
           seconds). Missing reader ⇒ 0 ⇒ nothing is ever evicted, i.e. exactly
           the old behaviour. */
        var readTime = (typeof opts.getCurrentTime === 'function') ? opts.getCurrentTime : function () { return 0; };
        function now() { var t = 0; try { t = +readTime() || 0; } catch (_) {} return t; };

        var cb = {
            attach: opts.onAttach || function () {},
            first: opts.onFirstSegment || function () {},
            prog: opts.onProgress || function () {},
            done: opts.onDone || function () {},
            err: opts.onError || function () {}
        };

        var audioIndex = (opts.audioIndex != null) ? (opts.audioIndex | 0) : null;
        var th = threadArgs(opts.vcodec);
        function segArgs(dur) {
            /* Which audio track gets baked in. Without an explicit map ffmpeg
               takes the container's default, so a file with a Japanese default
               and a Russian dub could only ever be watched in Japanese on this
               tier. */
            var map = (hasAudio && audioIndex != null)
                ? ['-map', '0:v:0', '-map', '0:a:' + (audioIndex | 0) + '?'] : [];
            return map.concat(['-t', String(dur)], segmentVideoArgs(scale, crf), hasAudio ? AUDIO_ARGS : ['-an'], MUX_ARGS, th.post);
        }

        /* ── The SourceBuffer is not infinite ────────────────────────────
           Chrome gives a SourceBuffer roughly 150 MB (less on phones). We
           appended and never evicted, so a long file simply hit the wall:
           measured on a 5-minute 1080p XVID, `appendBuffer` threw
           QuotaExceededError at exactly 2:00 with 145 MB buffered, the loop
           `break`-ed, and the viewer was told "converted as far as it could —
           the rest of the file could not be read". The file was fine. In
           production this ended 55 sessions in 60 days, on average 7 minutes
           in, and every one of them read as a finished job (green text).

           Every streaming player answers this the same way: drop what is
           already behind the viewer and try again. Keep-behind shrinks on each
           attempt, because on a phone even 60 s of 1080p may not be enough to
           free room. */
        var EVICT_KEEP_BEHIND = [60, 20, 5];
        /* Below this a produced segment carries no real frames — see the
           measurement at the end-of-source check. */
        var EMPTY_SEGMENT_BYTES = 64 * 1024;

        function evictTo(target) {
            return new Promise(function (res) {
                if (!sb || stopped || sb.updating) return res(false);
                var first = 0;
                try { first = sb.buffered.length ? sb.buffered.start(0) : 0; } catch (_) { return res(false); }
                if (!(target > first + 1)) return res(false);   // nothing worth dropping
                function fin() { sb.removeEventListener('updateend', fin); res(true); }
                sb.addEventListener('updateend', fin);
                try { sb.remove(first, target); }
                catch (_) { sb.removeEventListener('updateend', fin); res(false); }
            });
        }

        /* How much of the timeline the SourceBuffer actually holds. The loop's
           own `pos` is only an INTENTION — what we asked ffmpeg for. When the
           source turns out to be shorter than we believed, the two diverge,
           and only this one is the truth. */
        function bufferedEnd() {
            try { return (sb && sb.buffered.length) ? sb.buffered.end(sb.buffered.length - 1) : 0; }
            catch (_) { return 0; }
        }

        function isQuota(e) {
            return !!e && (e.name === 'QuotaExceededError' || /quota|SourceBuffer is full/i.test(e.message || ''));
        }

        /* Append, and when the buffer is full make room instead of giving up.
           Returns 'ok' | 'wait' — 'wait' means nothing could be freed because
           the viewer has not moved yet, so the converter should idle rather
           than treat a full buffer as a broken file. */
        async function appendManaged(buf) {
            try { await append(buf); return 'ok'; }
            catch (e) { if (!isQuota(e)) throw e; }
            for (var i = 0; i < EVICT_KEEP_BEHIND.length; i++) {
                var target = now() - EVICT_KEEP_BEHIND[i];
                var freed = await evictTo(target);
                if (!freed) continue;
                try { await append(buf); 
                    if (typeof window !== 'undefined' && typeof window.__aeFeature === 'function') {
                        try { window.__aeFeature('mse_evicted', { keep: EVICT_KEEP_BEHIND[i], at: Math.round(now()), ready: Math.round(readySec) }); } catch (_) {}
                    }
                    return 'ok';
                }
                catch (e2) { if (!isQuota(e2)) throw e2; }
            }
            return 'wait';
        }

        function append(buf) {
            return new Promise(function (res, rej) {
                if (!sb || stopped) return res();
                function ok() { cleanup(); res(); }
                /* An 'error' Event carries no .message, so rejecting with it
                   reached telemetry as an EMPTY reason and made this whole
                   failure class unreadable in the data. Reject with a real
                   Error that names the state instead. */
                function bad(e) {
                    cleanup();
                    var why = (e && e.message) ? e.message
                            : ('append_failed (mime=' + mime + ', mediaSource=' + (ms && ms.readyState) + ')');
                    rej(new Error(why));
                }
                function cleanup() {
                    sb.removeEventListener('updateend', ok);
                    sb.removeEventListener('error', bad);
                }
                sb.addEventListener('updateend', ok);
                sb.addEventListener('error', bad);
                try { sb.appendBuffer(buf); } catch (e) { cleanup(); rej(e); }
            });
        }

        /* A MediaSource does NOT open just because an object URL exists for it:
           `sourceopen` fires only once that URL is ATTACHED to a media element.
           Creating the URL and then waiting for sourceopen therefore deadlocks
           until the timeout — measured, it errored at ~18 s and the caller
           quietly fell back to the whole-file path, so progressive looked
           "working" while never once running. Hence: hand the URL out FIRST via
           onAttach so the caller can mount its player, and only then wait. */
        var cancelOpenDeadline = function () {};
        var ready = new Promise(function (resolve, reject) {
            if (!isSupported()) return reject(new Error('progressive_unsupported'));
            ms = new MediaSource();
            url = URL.createObjectURL(ms);
            ms.addEventListener('sourceopen', function () {
                cancelOpenDeadline();
                try {
                    sb = ms.addSourceBuffer(mime);
                    // 'sequence' lays each segment straight after the previous
                    // one, so independently-encoded segments (each starting at
                    // t=0) do not all pile up at the head of the timeline.
                    sb.mode = 'sequence';
                    // Without this the element's duration is NaN and the scrub
                    // bar cannot represent the film at all — it grows as
                    // segments land, so the viewer has no idea how long the
                    // thing is. Set from the probe, which knows.
                    if (duration) { try { ms.duration = duration - startAt; } catch (_) {} }
                    resolve();
                } catch (e) { reject(e); }
            }, { once: true });
            // let the caller attach on the next tick, before we start waiting
            setTimeout(function () { try { cb.attach({ url: url }); } catch (e) { reject(e); } }, 0);
            cancelOpenDeadline = visibleDeadline(function () {
                if (!sb) reject(new Error('sourceopen_timeout'));
            }, 20000);
        });
        /* A MediaSource that will never open is the end of the run even while a
           segment is still being encoded — say so now, not minutes later at the
           first append. */
        ready.catch(function (e) {
            if (done || stopped) return;
            done = true; stopped = true; failed = true;
            try { if (window.FFmpegProcessor && window.FFmpegProcessor.cancel) window.FFmpegProcessor.cancel(); } catch (_) {}
            cb.err(e);
        });

        /* The encoder does NOT wait for the element. Before, the pump started
           only after `sourceopen`; with the tab hidden that meant nothing at
           all happened until the viewer came back, and then the whole first
           segment (tens of seconds to minutes) was still ahead of them. Now the
           work runs while they are away and waits for the MediaSource only at
           the first append. */
        var loop = (async function () {
            if (!isSupported()) { await ready; return; }
            if (!window.FFmpegProcessor.isReady()) await window.FFmpegProcessor.init();
            var pos = startAt;
            while (!stopped) {
                /* FFmpegProcessor is one worker with no queue: two pumps
                   calling exec() at the same time corrupt each other's run.
                   The audio-track sidecar borrows the engine for ~1 s at a
                   time (a 15 s audio segment), so yielding between segments is
                   enough — and it is the video pump that yields, because the
                   viewer is waiting on the sound, not on picture they already
                   have buffered. */
                while (paused && !stopped) { await new Promise(function (r) { setTimeout(r, 150); }); }
                if (stopped) break;
                if (duration && pos >= duration - 0.25) break;
                var want = duration ? Math.min(segLen, duration - pos) : segLen;
                var t0 = (window.performance || Date).now();
                var out;
                try {
                    // -ss BEFORE -i: seek then decode. As an output option it
                    // decodes and discards everything ahead of the segment, and
                    // each segment then costs more than the last — measured
                    // decay 0.97x -> 0.38x realtime across four segments.
                    out = await window.FFmpegProcessor.processStreaming(
                        file, segArgs(want), 'seg.mp4', undefined, th.pre.concat(['-ss', String(pos)]));
                } catch (e) {
                    if (segCount === 0) throw e;      // nothing playable at all
                    /* Mid-file the converter used to just `break`, and the tool
                       said "the rest of the file could not be read" — blaming
                       the source for OUR engine dying, with no telemetry at
                       all, so the size of this class was unknowable. Name it. */
                    engineError = String((e && e.message) || e).slice(0, 160);
                    break;                            // partial is still watchable
                }
                if (stopped) break;
                var ms1 = (window.performance || Date).now() - t0;
                lastX = want / (ms1 / 1000);

                var bytes = new Uint8Array(await out.blob.arrayBuffer());
                if (stopped) break;
                // The element may still be deferring its load (hidden tab):
                // the first segment waits here, not the encoder.
                if (!sb) await ready;
                if (stopped) break;
                var endBefore = bufferedEnd();
                var placed;
                try { placed = await appendManaged(bytes); }
                catch (e) { if (segCount === 0) throw e; break; }
                while (placed === 'wait' && !stopped) {
                    /* The buffer is full and there is nothing behind the viewer
                       to drop, because the viewer has not watched that far. The
                       converter is simply too far ahead — that is not an error
                       and above all not "the file cannot be read". Idle until
                       they move.

                       No deadline: giving up after N minutes would mean a
                       viewer who paused to make tea comes back to a dead job
                       and a message promising it would continue. stop() (file
                       change, reset, navigation) is what ends this. Polling
                       backs off, and a hidden tab throttles it to almost
                       nothing anyway. */
                    cb.prog({ readySec: readySec, xRealtime: lastX, duration: duration,
                              segments: segCount, waiting: true });
                    var idle = 500;
                    while (!stopped) {
                        await new Promise(function (r) { setTimeout(r, idle); });
                        idle = Math.min(2000, idle + 250);
                        var t = now();
                        if (t > 0 && (readySec - t) < 30) break;
                    }
                    if (stopped) break;
                    cb.prog({ readySec: readySec, xRealtime: lastX, duration: duration,
                              segments: segCount, waiting: false });
                    try { placed = await appendManaged(bytes); }
                    catch (e2) { placed = 'fail'; break; }
                }
                if (stopped) break;
                if (placed !== 'ok') { stalledOnBuffer = (placed === 'wait' || placed === 'fail'); break; }

                /* Stop when segments stop adding anything. Without a known
                   duration the loop had NO exit condition at all: measured on a
                   60-second file driven with duration 0, it produced 143
                   segments claiming 2145 s "ready" and would have run forever,
                   burning a phone's battery on empty output — and production
                   shows a session reaching 2190 s the same way. Two barren
                   segments in a row, so a single anomaly cannot end a good
                   file early. */
                var grew = bufferedEnd() - endBefore;
                if (grew < 0.05) {
                    /* Two ways a segment can add nothing, and they must not be
                       confused. Past the end of the source ffmpeg returns a
                       husk — measured on a 60 s file with 15 s segments: real
                       segments 3.56–3.63 MB, the one straddling the end
                       18.9 KB, the one past it 1.3 KB, a ~200x gap. Real bytes
                       that fail to extend the timeline are something else
                       (overlapping timestamps, a half-written segment), and
                       calling that "the file ended" would be the same silent
                       truncation this module already fixed once. */
                    if (bytes.length < EMPTY_SEGMENT_BYTES) {
                        if (++barren >= 2) { atEof = true; break; }
                    } else if (++barren >= 2) {
                        break;                       // stops, but NOT as "complete"
                    }
                } else { barren = 0; }

                pos += want;
                /* With no duration to trust, say what is really there. */
                readySec = duration ? pos - startAt : Math.max(bufferedEnd(), 0);
                segCount++;
                if (segCount === 1) cb.first({ url: url, xRealtime: lastX });
                cb.prog({ readySec: readySec, xRealtime: lastX, duration: duration, segments: segCount });
                /* Yield. Without this the loop runs segment after segment in
                   one go: the element never gets a turn to start decoding, the
                   status line never paints, and the viewer waits for the whole
                   file anyway — which is the exact thing this module exists to
                   avoid. The first pause is longer to let playback actually
                   begin before we compete for the CPU again. */
                await new Promise(function (r) { setTimeout(r, segCount === 1 ? 400 : 30); });
            }
            if (failed) return;
            done = true;
            try {
                if (ms && ms.readyState === 'open' && !stopped) ms.endOfStream();
            } catch (_) {}
            cb.done({
                readySec: readySec,
                /* `!duration` used to count as complete, so a file whose
                   duration we could not read announced "whole file ready" no
                   matter why it stopped.
                   ⚠️ And "the segments went barren" is NOT by itself the end of
                   the film: a damaged stretch in the middle looks identical.
                   With a duration to compare against, only reaching it counts;
                   without one, running dry is the only end we can observe. */
                complete: (!!duration && readySec >= (duration - startAt) - 0.5) || (atEof && !duration),
                /* An incomplete run has two very different causes and the tool
                   must not describe them with one sentence: the source ran out
                   of readable data, or OUR buffer did. */
                reason: engineError ? 'engine' : (stalledOnBuffer ? 'buffer_full' : 'source'),
                err: engineError || ''
            });
        })().catch(function (e) {
            if (done) return;
            done = true;
            if (!stopped) cb.err(e);
        });

        return {
            url: function () { return url; },
            readySeconds: function () { return readySec; },
            throughput: function () { return lastX; },
            isDone: function () { return done; },
            /* Change the picture size for SUBSEQUENT segments. Existing ones
               keep their size; the browser scales them to the element, so the
               switch is seamless to the viewer. */
            setScale: function (px) { scale = +px || 0; },
            getScale: function () { return scale; },
            stop: function () {
                stopped = true;
                try { if (window.FFmpegProcessor.cancel) window.FFmpegProcessor.cancel(); } catch (_) {}
                try { if (url) URL.revokeObjectURL(url); } catch (_) {}
            },
            whenReady: function () { return ready; },
            whenDone: function () { return loop; },
            /* Hand the shared FFmpeg worker to somebody else between segments.
               The picture already buffered keeps playing while paused. */
            pause: function () { paused = true; },
            resume: function () { paused = false; },
            isPaused: function () { return paused; }
        };
    }

    window.ProgressiveDecode = {
        start: start,
        measure: measure,
        isSupported: isSupported,
        MIME: AUDIO_MIME,
        VIDEO_MIME: VIDEO_MIME,
        _args: { VIDEO_ARGS: VIDEO_ARGS, AUDIO_ARGS: AUDIO_ARGS, MUX_ARGS: MUX_ARGS },
        _threadArgs: threadArgs,
        mtEstimate: mtEstimate,
        _segmentVideoArgs: segmentVideoArgs
    };
})();
